import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Paper, Stack, Typography } from "@mui/material";
import { format } from "date-fns";
import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useRetrieveUserLeagueMatchStatsPermissions } from "../api/PermissionApiProvider";
import { useRetrieveLeagueMatch, useSubmitLeagueMatchStats, } from "../api/SeasonApiProvider";
import { useRetrievePlayersByTeamId } from "../ApiProvider";
import { ErrorView } from "../nav/Error";
import { Loading } from "../nav/Loading";
import { NotFound } from "../nav/NotFound";
import ErrorSnackbar from "../utils/ErrorSnackbar";
import { useNumericParams } from "../utils/useNumericParams";
import { LeagueMatchStatsForm } from "./LeagueMatchStatsForm";
import { toMatchPlayerStat } from "./utils";
export const UpdatePlayerLeagueMatchStats = () => {
    const { id } = useNumericParams();
    const { data: leagueMatch, isLoading: leagueMatchIsLoading, error: leagueMatchError, } = useRetrieveLeagueMatch({ id });
    // Get league match stats permissions
    const { data: leagueMatchStatsPermissions, isLoading: leagueMatchStatsPermissionsIsLoading, error: leagueMatchStatsPermissionsError, } = useRetrieveUserLeagueMatchStatsPermissions({
        leagueMatchId: id,
    });
    const { canModifyTeams = [], canModifyResult = false } = leagueMatchStatsPermissions || {};
    // Check if user can modify either team (is a team manager or league admin)
    const homeTeamId = leagueMatch?.homeTeam?.id;
    const awayTeamId = leagueMatch?.awayTeam?.id;
    const canModifyHomeTeam = homeTeamId
        ? canModifyTeams.includes(homeTeamId)
        : false;
    const canModifyAwayTeam = awayTeamId
        ? canModifyTeams.includes(awayTeamId)
        : false;
    const { data: homeTeamPlayers, error: homeTeamPlayersError, isLoading: homeTeamPlayersIsLoading, } = useRetrievePlayersByTeamId({ teamId: homeTeamId });
    const { data: awayTeamPlayers, error: awayTeamPlayersError, isLoading: awayTeamPlayersIsLoading, } = useRetrievePlayersByTeamId({ teamId: awayTeamId });
    const navigate = useNavigate();
    const { isLoading: submitStatsIsLoading, isError: submitStatsIsError, mutate: submitStats, } = useSubmitLeagueMatchStats(leagueMatch?.id);
    if (leagueMatchIsLoading || leagueMatchStatsPermissionsIsLoading) {
        return _jsx(Loading, {});
    }
    if (leagueMatchError) {
        return _jsx(ErrorView, { error: leagueMatchError });
    }
    if (leagueMatchStatsPermissionsError) {
        return _jsx(ErrorView, { error: leagueMatchStatsPermissionsError });
    }
    // User needs to be league admin or team manager to access this page
    // For now, we'll allow anyone who can view the league (simplified permission check)
    if (canModifyTeams.length === 0) {
        return _jsx(NotFound, {});
    }
    if ((homeTeamId && homeTeamPlayersIsLoading) ||
        (awayTeamId && awayTeamPlayersIsLoading) ||
        !leagueMatch) {
        return _jsx(Loading, {});
    }
    const navigateToMatchList = () => {
        // Land on the scores gameweek once the match has a result; otherwise (e.g.
        // a manager entering stats before the admin records the score) go to
        // fixtures — either way, scoped to this match's gameweek.
        const hasResult = canModifyResult || leagueMatch.leagueMatchResult !== null;
        const base = `/leagues/${leagueMatch.leagueId}/seasons/${leagueMatch.seasonId}`;
        const query = `division=${leagueMatch.division}&gameweek=${leagueMatch.round}`;
        navigate(hasResult
            ? `${base}?tab=scores&${query}`
            : `${base}?tab=fixtures&${query}`);
    };
    const onSubmit = (leagueMatchResult, playerLeagueMatchStats) => {
        submitStats({
            // Only the league admin may set the result; managers submit stats only.
            result: canModifyResult
                ? {
                    homeTeamScore: leagueMatchResult.homeTeamScore,
                    awayTeamScore: leagueMatchResult.awayTeamScore,
                    matchReport: leagueMatchResult.matchReport,
                }
                : null,
            playerStats: Object.values(playerLeagueMatchStats).map((stat) => ({
                ...toMatchPlayerStat(stat),
                teamId: stat.teamId,
            })),
        }, { onSuccess: navigateToMatchList });
    };
    if (homeTeamPlayersError ||
        awayTeamPlayersError ||
        (homeTeamId && homeTeamPlayers === undefined) ||
        (awayTeamId && awayTeamPlayers === undefined)) {
        return _jsx(ErrorView, { error: homeTeamPlayersError || awayTeamPlayersError });
    }
    return (_jsxs(React.Fragment, { children: [_jsx(ErrorSnackbar, { open: submitStatsIsError, message: "Failed to save league match stats! Please try again." }), _jsx(Paper, { elevation: 0, sx: { p: 2, mb: 3, bgcolor: "grey.50", borderRadius: 2 }, children: _jsxs(Stack, { spacing: 1, alignItems: "center", children: [_jsx(Typography, { variant: "body2", color: "text.secondary", children: format(new Date(leagueMatch.kickOff), "EEEE, MMMM do yyyy • h:mm a") }), _jsxs(Stack, { direction: "row", spacing: 2, alignItems: "center", children: [_jsx(Typography, { component: Link, to: `/teams/${leagueMatch.homeTeam.id}`, variant: "h6", fontWeight: 600, sx: {
                                        textDecoration: "none",
                                        color: "text.primary",
                                        "&:hover": { color: "primary.main" },
                                    }, children: leagueMatch.homeTeam.name }), _jsx(Typography, { variant: "h6", color: "text.secondary", children: "v" }), _jsx(Typography, { component: Link, to: `/teams/${leagueMatch.awayTeam.id}`, variant: "h6", fontWeight: 600, sx: {
                                        textDecoration: "none",
                                        color: "text.primary",
                                        "&:hover": { color: "primary.main" },
                                    }, children: leagueMatch.awayTeam.name })] }), leagueMatch.venue?.name && (_jsxs(Typography, { variant: "body2", color: "text.secondary", children: [leagueMatch.venue.name, leagueMatch.pitchName && ` - ${leagueMatch.pitchName}`] }))] }) }), _jsx(LeagueMatchStatsForm, { homeTeamPlayers: (homeTeamPlayers ?? []).filter((p) => p.isActive), awayTeamPlayers: (awayTeamPlayers ?? []).filter((p) => p.isActive), onSubmit: onSubmit, leagueMatch: leagueMatch, canModifyHomeTeam: canModifyHomeTeam, canModifyAwayTeam: canModifyAwayTeam, canModifyResult: canModifyResult, isSubmitting: submitStatsIsLoading })] }));
};
